import {
  recordErrorStage,
  recordFunnelStep,
  recordLatency,
  recordPageDwell,
  recordPageView,
  type RumMetadata,
} from './rum';
import type { V2OnboardingState } from './v2Onboarding';

export const V2_FUNNEL_ID = 'v2';

export type WorkspacePage =
  | 'overview'
  | 'insights'
  | 'roadmap'
  | 'roster'
  | 'graph'
  | 'documents'
  | 'changelog';

export function resolveWorkspacePage(pathname: string): WorkspacePage {
  if (pathname.includes('/insights')) return 'insights';
  if (pathname.includes('/timeline') || pathname.includes('/gates') || pathname.includes('/roadmap')) {
    return 'roadmap';
  }
  if (pathname.includes('/people')) return 'roster';
  if (pathname.includes('/graph')) return 'graph';
  if (pathname.includes('/documents')) return 'documents';
  if (pathname.includes('/changelog')) return 'changelog';
  return 'overview';
}

export function withV2Metadata(metadata?: RumMetadata, projectId?: string): RumMetadata {
  return {
    funnel_id: V2_FUNNEL_ID,
    ...(projectId ? { project_id: projectId } : {}),
    ...(metadata || {}),
  };
}

export function recordV2FunnelStep(
  stepName: string,
  stepStatus: 'start' | 'success' | 'failure' | 'blocked' | 'abandon' | 'shown' | 'click',
  metadata?: RumMetadata,
  projectId?: string
): void {
  recordFunnelStep(stepName, stepStatus, withV2Metadata(metadata, projectId));
}

export function recordV2PageView(pageName: string, metadata?: RumMetadata, projectId?: string): void {
  recordPageView(pageName, withV2Metadata(metadata, projectId));
}

export function recordV2PageDwell(
  pageName: string,
  durationMs: number,
  metadata?: RumMetadata,
  projectId?: string
): void {
  recordPageDwell(pageName, durationMs, withV2Metadata(metadata, projectId));
}

export function recordV2Latency(
  operation: string,
  durationMs: number,
  metadata?: RumMetadata,
  projectId?: string
): void {
  recordLatency(operation, durationMs, withV2Metadata(metadata, projectId));
}

export function recordV2ErrorStage(
  errorStage: string,
  error: unknown,
  metadata?: RumMetadata,
  projectId?: string
): void {
  recordErrorStage(errorStage, error, withV2Metadata(metadata, projectId));
}

export const ONBOARDING_STEP_NAMES: Record<number, string> = {
  1: 'v2_onboarding_step_parcel',
  2: 'v2_onboarding_step_development_paths',
  3: 'v2_onboarding_step_deal_context',
  4: 'v2_onboarding_step_priorities',
};

export function hasOnboardingState(state: V2OnboardingState): boolean {
  return Boolean(
    state.address?.trim() ||
      state.parcelPin?.trim() ||
      state.pendingCreate ||
      state.redirect?.includes('onboarding=1')
  );
}

export function onboardingRumContext(state: V2OnboardingState): RumMetadata {
  return withV2Metadata({
    has_onboarding_state: hasOnboardingState(state),
    location_mode: state.locationMode || 'address',
    pending_create: Boolean(state.pendingCreate),
  });
}
